import { useEffect, useState } from "react";
import { ScrollText, Search } from "lucide-react";

import { auditLogsApi } from "@/api/audit";
import { useAuth } from "@/auth/useAuth";
import { Alert } from "@/components/Alert";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { FormSelect } from "@/components/FormSelect";
import { ListToolbar } from "@/components/ListToolbar";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { SectionCards } from "@/components/section-cards";
import { Badge } from "@/components/ui/badge";

const CATEGORIES = [
  { value: "", label: "All Categories" },
  { value: "auth", label: "Authentication & Security" },
  { value: "directory", label: "Clients & Caregivers" },
  { value: "scheduling", label: "Visits & Schedules" },
  { value: "careplans", label: "Care Plans" },
  { value: "organization", label: "Organization Admin" },
  { value: "system", label: "System Events" },
];

function actionVariant(action) {
  if (action.includes("LOGIN")) return "default";
  if (action.includes("DELETE") || action.includes("CANCEL")) return "outline";
  if (action.includes("LOGOUT")) return "secondary";
  return "outline";
}

export function AuditLogsPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);

  const fetchAuditData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [logsRes, metricsRes] = await Promise.all([
        auditLogsApi.list({ search, category, page }),
        auditLogsApi.getMetrics(),
      ]);

      const logsList = Array.isArray(logsRes) ? logsRes : logsRes.results || [];
      setLogs(logsList);
      setMetrics(metricsRes);
    } catch (err) {
      setError(err.message || "Failed to load audit logs.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditData();
  }, [search, category, page]);

  const metricCards = [
    {
      title: "Total Audit Events",
      value: String(metrics?.total_logs ?? "—"),
      footer: "Logged activity entries",
      hint: "All recorded platform events",
    },
    {
      title: "Sign-ins Today",
      value: String(metrics?.auth_logins_today ?? "—"),
      footer: "Successful authentication logins",
      hint: "Counted for the current day",
    },
    {
      title: "Data Mutations",
      value: String(metrics?.mutations_count ?? "—"),
      footer: "Created or updated records",
      hint: "Write actions across modules",
    },
    {
      title: "Active Actors",
      value: String(metrics?.active_actors_count ?? "—"),
      footer: "Unique users logging actions",
      hint: "Distinct actors in the trail",
    },
  ];

  const columns = [
    {
      key: "created_at",
      header: "Timestamp",
      render: (row) => {
        const eventTime = new Date(row.created_at).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
        return (
          <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">
            {eventTime}
          </span>
        );
      },
    },
    {
      key: "actor",
      header: "Actor User",
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-[0.7rem] font-semibold">
            {(row.actor_name || row.actor_email || "U").substring(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{row.actor_name || "System"}</div>
            <div className="truncate text-xs text-muted-foreground">
              {row.actor_email || "N/A"}
            </div>
          </div>
        </div>
      ),
    },
    ...(user?.is_superuser
      ? [
          {
            key: "agency",
            header: "Organization",
            render: (row) => <Badge variant="outline">{row.agency_name}</Badge>,
          },
        ]
      : []),
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <Badge variant={actionVariant(row.action)}>{row.action}</Badge>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (row) => (
        <span className="text-xs capitalize text-muted-foreground">{row.category}</span>
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (row) => (
        <div className="max-w-xs text-sm leading-snug">
          {row.description}
          {row.target_type ? (
            <div className="mt-0.5 text-xs text-muted-foreground">
              Target: {row.target_type} ({row.target_id?.slice(0, 8)})
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: "ip",
      header: "IP Address",
      render: (row) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.ip_address || "Internal"}
        </span>
      ),
    },
  ];

  if (isLoading && logs.length === 0 && !metrics) {
    return <PageSkeleton />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Access & Audit Logs"
        description={
          user?.is_superuser
            ? "Platform-wide security audit trail and access logs across all tenant organizations."
            : `Access and security audit logs for ${user?.agency?.name || "your organization"}.`
        }
      />

      <SectionCards cards={metricCards} />

      <PageBody>
        <ListToolbar
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          searchPlaceholder="Search by actor, action, or description…"
          searchAriaLabel="Search audit logs"
        >
          <div className="w-full max-w-xs">
            <FormSelect
              id="audit-category-filter"
              value={category}
              onValueChange={(v) => {
                setCategory(v);
                setPage(1);
              }}
              options={CATEGORIES}
              placeholder="All Categories"
            />
          </div>
        </ListToolbar>

        <Alert>{error}</Alert>

        {logs.length === 0 ? (
          <EmptyState
            icon={search || category ? Search : ScrollText}
            tone={search || category ? "slate" : "slate"}
            title={
              search || category
                ? "No log entries match those filters."
                : "No audit events yet"
            }
            message={
              search || category
                ? "Try a different search or category."
                : "Events appear here as users sign in and change records."
            }
          />
        ) : (
          <div aria-busy={isLoading}>
            <DataTable columns={columns} rows={logs} getRowKey={(row) => row.id} />
          </div>
        )}
      </PageBody>
    </PageShell>
  );
}
